import { Checkbox, List, Modal, SearchBar, TextareaItem } from "antd-mobile";
import { Popup } from "antd-mobile-v5";
import {
  CSSProperties,
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "../../util/axios";

interface SelectItemProps {
  dictionary: string;
  value?: string;
  onChange?: (value: any) => void;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  originFilter?: string;
  children?: any;
  title?: string;
  multiple?: boolean;
}

const style: CSSProperties = {
  height: 300,
  overflowY: "auto",
};

let timeout: any = null;

const DictionarySelect = forwardRef(
  (
    {
      dictionary,
      value,
      onChange,
      disabled,
      required,
      placeholder,
      children,
      title,
      originFilter,
      multiple = true,
    }: SelectItemProps,
    ref: any
  ) => {
    const divRef = useRef<any>();
    const [visible, setVisible] = useState<boolean>(false);
    const [pageIndex, setPageIndex] = useState(0);
    const [finished, setFinished] = useState<boolean>(false);
    const [filter, setFilter] = useState<string>("");
    const [checkedOptions, setCheckedOptions] = useState<
      { uuid: string; name: string }[]
    >([]);
    const [options, setOptions] = useState<
      {
        name: string;
        uuid: string;
      }[]
    >([]);

    let dictionaryName = dictionary;
    let colName = "";
    if (dictionary.indexOf(".")) {
      dictionaryName = dictionary.split(".")[0];
      colName = dictionary.split(".")[1];
    }

    const getOptions = useCallback(
      async (filter, pageIndex) => {
        if (originFilter) {
          if (filter) {
            filter += " and " + originFilter;
          } else {
            filter += originFilter;
          }
        }
        const resp = await axios.post<any>(
          `/crm/common/CommonApi/GetZLSelectDict?dictionaryName=${dictionaryName}&filter=${filter}&pageIndex=${pageIndex}&pageSize=10&colName=${
            colName || ""
          }`
        );
        if (resp.data.Status === "1") {
          if (resp.data.Data.length < 10) {
            setFinished(true);
          }

          if (pageIndex === 0) {
            setOptions(resp.data.Data);
          } else {
            setOptions((prevState) => [...prevState, ...resp.data.Data]);
          }
        }
      },
      [colName, dictionaryName, originFilter]
    );

    useEffect(() => {
      getOptions(filter, pageIndex).then();
    }, [filter, getOptions, pageIndex, setFilter]);

    useEffect(() => {
      (async () => {
        if (!value) {
          setCheckedOptions([]);
          return;
        }
        const resp = await axios.get<any>(
          `/crm/common/CommonApi/GetZLSelectDict?dictionaryName=${dictionaryName}&selectId=${value}&filter=setValue&isFull=true`
        );
        setCheckedOptions(resp.data.Data);
      })();
    }, [dictionaryName, value]);

    const divScroll = useCallback(
      async (e) => {
        const isScrollToBottom =
          e.currentTarget.scrollHeight -
            e.currentTarget.offsetHeight -
            e.currentTarget.scrollTop ===
          0;
        if (isScrollToBottom) {
          if (finished) {
            return;
          }

          setPageIndex((prevState) => prevState + 1);
        }
      },
      [finished]
    );

    const searchBarChange = useCallback((value) => {
      timeout && clearTimeout(timeout);
      timeout = setTimeout(() => {
        divRef.current.scrollTo(0, 0);
        setPageIndex(0);
        setFinished(false);
        const tempFilter = "name like '%" + value + "%'";
        setFilter(encodeURI(tempFilter));
      }, 1000);
    }, []);

    return (
      <>
        {children && (
          <div
            style={{
              display: "flex",
            }}>
            {required && (
              <span className="adm-form-item-label-required">*</span>
            )}
            {children}
          </div>
        )}
        <TextareaItem
          title={title}
          placeholder={placeholder}
          className="border-bottom-d9 text-align-left"
          value={checkedOptions.map((item) => item.name).join(",")}
          onFocus={() => {
            if (!disabled) {
              setVisible(true);
            }
          }}
          editable={false}
          disabled={disabled}
          autoHeight
        />
        <Popup
          visible={visible}
          position="bottom"
          onMaskClick={() => {
            setVisible(false);
          }}>
          <List renderHeader={() => children}>
            <SearchBar placeholder={placeholder} onChange={searchBarChange} />
            <div onScroll={divScroll} style={style} ref={divRef}>
              {options.map((option) => (
                <Checkbox.CheckboxItem
                  checked={
                    checkedOptions.findIndex(
                      (item) => item.uuid === option.uuid
                    ) > -1
                  }
                  key={option.uuid}
                  onClick={(e: any) => {
                    let newValue: { uuid: string; name: string }[];
                    if (e.target.checked) {
                      if (multiple) {
                        setCheckedOptions((prevState) => {
                          const index = prevState.findIndex(
                            (item) => item.uuid === option.uuid
                          );
                          if (index < 0) {
                            newValue = [...checkedOptions, option];
                            return newValue;
                          } else {
                            newValue = prevState;
                            return newValue;
                          }
                        });
                      } else {
                        newValue = [option];
                        setCheckedOptions(newValue);
                      }
                    } else {
                      if (!multiple) {
                        newValue = [];
                        setCheckedOptions(newValue);
                      } else {
                        setCheckedOptions((prevState) => {
                          const index = prevState.findIndex(
                            (item) => item.uuid === option.uuid
                          );
                          const cloned = [...prevState];
                          cloned.splice(index, 1);
                          newValue = cloned;
                          return newValue;
                        });
                      }
                    }
                    onChange?.(newValue!.map((item) => item.uuid).join());
                  }}>
                  {option.name}
                </Checkbox.CheckboxItem>
              ))}
            </div>
          </List>
        </Popup>
      </>
    );
  }
);

export default DictionarySelect;
